import {lazy, Suspense, useContext, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMountedState} from '@s-ui/react-hooks'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'

import Button, {searchSortButtonDesigns} from './button.js'
import {PLACES, SEARCH_USE_CASE_BY_PLACE} from './config.js'
import SortOptions from './sort-options.js'

const SortModal = lazy(() => import('./modal.js'))

const DEFAULT_MODAL_HEADER = 'SORT_MODAL_HEADER'
const TECHNICAL_MODAL_HEADER = 'SORT_TECHNICAL_DETAILS_MODAL_HEADER'
const DEALERS_LIST_MODAL_HEADER = 'SORT_DEALERS_LIST_MODAL_HEADER'

const linkFactoryDefaultProp = ({children, ...rest} = {}) => <a {...rest}>{children}</a>

const SearchSort = ({
  dealerSlug,
  design,
  initialShow = false,
  linkFactory = linkFactoryDefaultProp,
  listingSectionId,
  pageName,
  place,
  search,
  sortConfigProps,
  withSortOrderInfo = false
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const getMountedState = useMountedState()
  const isMounted = getMountedState()
  const [show, setShow] = useState(initialShow)
  const openModal = () => setShow(true)
  const closeModal = () => setShow(false)
  const DEFAULT_PAGE_NUMBER = config.get('DEFAULT_PAGE_NUMBER')
  const FILTER_NAMES = config.get('FILTER_NAMES')
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const {
    SORT,
    SORT_BY_CATEGORY1_ID,
    ADS_LIST_SUBSCRIPTION_SORT,
    MODELS_LIST_SORT,
    TECHNICAL_DETAILS_SORT,
    DEALERS_LIST_SORT
  } = config.get('search')
  const {
    ADS_LIST_PAGE_NAME: adsListPageName,
    ADS_LIST_SUBSCRIPTION_PAGE_NAME: adsListSubscriptionPageName = '',
    ADS_LIST_BUSINESS_SUBSCRIPTION_PAGE_NAME: adsListBusinessSubscriptionPageName = '',
    MAKES_DISTRIBUTOR_PAGE_NAME: makesDistributorPageName = '',
    TECHNICAL_DETAILS_SEARCH_PAGE_NAME: technicalDetailsSearchPageName,
    TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME: technicalDetailsSearchResultsPageName,
    PROFESSIONAL_STOCK_PAGE_NAME: professionalStockPageName,
    PROFESSIONAL_STOCK_RENTING_PAGE_NAME: professionalStockRentingPageName,
    DEALERS_LIST_PAGE_NAME: dealersListPageName
  } = config.get('page')
  const {categoryId} = search
  const hasCategoryId = Boolean(categoryId)

  const delayToNextFrame = useDelayToNextFrame()

  let useCase
  let title
  switch (pageName) {
    case adsListSubscriptionPageName:
    case adsListBusinessSubscriptionPageName:
    case makesDistributorPageName:
    case professionalStockRentingPageName:
      title = DEFAULT_MODAL_HEADER
      useCase = 'get_search_url_ads_use_case'
      break
    case professionalStockPageName:
      title = DEFAULT_MODAL_HEADER
      useCase = 'get_stock_url_params_search_use_case'
      break
    case technicalDetailsSearchPageName:
    case technicalDetailsSearchResultsPageName:
      title = TECHNICAL_MODAL_HEADER
      useCase = 'get_search_url_vehicles_use_case'
      break
    case dealersListPageName:
      title = DEALERS_LIST_MODAL_HEADER
      useCase = 'get_search_url_dealer_use_case'
      break
    default:
      title = DEFAULT_MODAL_HEADER
      useCase = 'get_search_url_ads_use_case'
      break
  }

  const financingSortConfig = SORT_BY_CATEGORY1_ID[categoryId] || SORT

  const _getSortConfig = () => {
    switch (pageName) {
      case adsListPageName:
        return financingSortConfig
      case adsListSubscriptionPageName:
      case adsListBusinessSubscriptionPageName:
      case professionalStockRentingPageName:
        return ADS_LIST_SUBSCRIPTION_SORT
      case makesDistributorPageName:
        return MODELS_LIST_SORT
      case professionalStockPageName:
        return SORT
      case technicalDetailsSearchPageName:
      case technicalDetailsSearchResultsPageName:
        return TECHNICAL_DETAILS_SORT
      case dealersListPageName:
        return DEALERS_LIST_SORT
      default:
        return hasCategoryId ? financingSortConfig : SORT
    }
  }
  const sortConfig = sortConfigProps ?? _getSortConfig()

  const handleSelectOption = ({sortBy, sortOrder}) => {
    closeModal()
    delayToNextFrame(() => {
      const newSearch = {...search, sortBy, sortOrder, page: DEFAULT_PAGE_NUMBER}
      domain
        .get(
          // Progressive rollout form new get url params search use case
          SEARCH_USE_CASE_BY_PLACE[place] || useCase
        )
        .execute({
          search: newSearch,
          pageName,
          dealerSlug,
          lastFilter: TRACKING_FILTER_NAMES[FILTER_NAMES.SORT_BY],
          listingSectionId
        })
    })
  }

  const buttonLabel = i18n.t(sortConfig[search.sortBy]?.label?.[search.sortOrder])

  return (
    <div className="mt-SearchSort">
      <Button
        design={design}
        label={buttonLabel}
        linkFactory={linkFactory}
        onClick={openModal}
        withSortOrderInfo={withSortOrderInfo}
      />
      {isMounted && show && (
        <Suspense fallback={<></>}>
          <SortModal isOpen={show} onClose={closeModal} title={i18n.t(title)} i18n={i18n}>
            <SortOptions search={search} sortConfig={sortConfig} onSelectOption={handleSelectOption} />
          </SortModal>
        </Suspense>
      )}
    </div>
  )
}

SearchSort.displayName = 'SearchSort'
SearchSort.propTypes = {
  dealerSlug: PropTypes.string,
  design: PropTypes.oneOf(Object.keys(searchSortButtonDesigns)),
  initialShow: PropTypes.bool,
  linkFactory: PropTypes.func,
  listingSectionId: PropTypes.string,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object.isRequired,
  sortConfigProps: PropTypes.object,
  withSortOrderInfo: PropTypes.bool
}

export {searchSortButtonDesigns, PLACES as searchSortPlaces}

export default SearchSort
